/**
 * Dispatch helper for ReportIssuePage.
 *
 * Picks the right v2 transition name based on (processState, action).
 * Sharetribe transitions take a single :from state, so what's
 * conceptually one transition ("cancel-at-pickup-issue") is split
 * into two named transitions whose source state differs. This
 * function maps the UI's action choice + current state to the
 * correct wire name.
 *
 * Required because picking the wrong transition name silently fails
 * with Sharetribe's "transition not allowed from current state"
 * error — exactly the kind of bug that only surfaces in production
 * unless explicitly tested.
 *
 * @param {string} processState  one of 'accepted' | 'kit-ready'
 * @param {string} action  one of 'continue' | 'cancel'
 * @returns {string|null}  transition name, or null if invalid combo
 */
export const pickReportIssueTransition = (processState, action) => {
  if (processState === 'kit-ready' && action === 'continue') {
    return 'transition/customer-start-rental-with-issue';
  }
  // Review-first refunds (process v10, owner-directed 2026-07-08): the
  // cancel choice REQUESTS a refund — no money moves until the operator
  // reviews the evidence. The legacy instant-refund transitions
  // (customer-cancel-at-pickup-issue*) are no longer fired by the UI.
  if (processState === 'kit-ready' && action === 'cancel') {
    return 'transition/customer-request-pickup-refund';
  }
  if (processState === 'accepted' && action === 'cancel') {
    return 'transition/customer-request-pickup-refund-pre-ready';
  }
  // 'accepted' + 'continue' is intentionally invalid — there's no
  // accepted → active-rental transition in the process. UI hides
  // the "continue" radio in that state. See design doc §10.5.
  return null;
};

/**
 * Whether a given action option is selectable for the current state.
 * Used by the UI to hide invalid radio options.
 */
export const isActionAvailable = (processState, action) => {
  return pickReportIssueTransition(processState, action) !== null;
};
